import { useEffect, useState } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import { useToast } from '../../toast/ToastContext';
import Pagination, { usePagination } from '../components/Pagination';

// §4.5 — all eleven guaranteed fields a template gets at creation
// (ICIB-10b): action, email, subsidised product level (is_product_field),
// effective date, reason, the five profile-mapped fields, and
// nz_citizen_or_visa (is_eligibility_field) — same guard set
// ICIB-10g/10h/10i reject a batch for touching, so this predicate decides
// both the "guaranteed" badge and whether Remove/toggle is offered on a row.
const GUARANTEED = (f) => f.is_action_field || f.is_email_field || f.is_product_field || f.is_effective_date_field || f.is_reason_field || f.is_eligibility_field || f.maps_to_user_field;
const FIELD_TYPES = ['text', 'email', 'phone', 'date', 'number', 'boolean', 'dropdown'];
const EMPTY_FIELD = { field_key: '', label: '', field_type: 'text', required: false, optionsText: '' };

// ICIB-10g/10h — a dropdown must carry `options` as a non-empty list of
// non-empty strings, and any other type must not carry it at all. The
// form takes one option per line (so an option can contain a comma);
// blank lines are dropped rather than sent, since the server rejects
// blank entries.
const parseOptions = (text) => text.split('\n').map((o) => o.trim()).filter(Boolean);

/* ICIB-10 — Manage a group's intake form template and fields.
   Template list (ICIB-10a) carries no fields — fields are fetched on
   demand per template (ICIB-10c, GET /form-templates/{id}) when a
   version's drawer is opened. */
function FormsView() {
  const {
    status, groupId, curGroup, createTemplateVersion, activateTemplate,
    templates: allTemplates, templatesStatus, templatesError, loadTemplates,
  } = useIcibAdmin();
  const { openDrawer, closeDrawer } = useDrawer();
  const toast = useToast();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);

  useEffect(() => { loadTemplates(groupId); }, [groupId, loadTemplates]);

  // Active template always first, then the rest newest-version-first.
  const templates = groupId ? allTemplates.filter((t) => t.company_id === groupId).slice().sort((a, b) => (b.is_active - a.is_active) || (b.version - a.version)) : [];
  const { page, setPage, pageCount, pageItems: pagedTemplates, total } = usePagination(templates);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const res = await createTemplateVersion(groupId, name);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not create this template.', 'bad', res.error); return; }
    toast(`${res.name} created (v${res.version}).`, 'ok');
    setName('');
  }

  // `key` forces a fresh drawer per version, and `onFollow` lets the drawer
  // reopen itself on the new version a structural field edit creates
  // (ICIB-10g/10h/10i) instead of staying on the old one.
  function open(t) {
    openDrawer(t.name, `ICIB-10c · v${t.version}${t.is_active ? ' · active' : ''}`, <TemplateDrawer key={t.id} gid={groupId} t={t} onClose={closeDrawer} onFollow={open} />);
  }

  // ICIB-10f straight from the list row — no need to open the drawer
  // first just to activate a version. There's no standalone "deactivate":
  // activating a different version automatically deactivates this one.
  async function onActivateRow(t) {
    setRowBusyId(t.id);
    const res = await activateTemplate(groupId, t.id);
    setRowBusyId(null);
    if (res?.error) toast(res.message || 'Could not activate this template.', 'bad', res.error);
    else toast('Now the active template for this company.', 'ok');
  }

  if (status === 'loading') return <div className="card"><div className="empty"><b>Loading…</b></div></div>;

  return (
    <>
      <div className="eyebrow">Enrolment form</div>
      <div className="page-h"><h2>Enrolment forms</h2></div>
      <p className="page-sub">The questions {curGroup()?.name || 'this company'} answers for every new person.</p>

      {templatesError && <div className="note bad" style={{ marginBottom: 14 }}><b>Couldn't load templates for this company</b>{templatesError}</div>}

      {!groupId ? (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      ) : (
        <div className="grid g2">
          <div>
            <div className="sec-h" style={{ marginTop: 0 }}><h3>All versions</h3><span className="spacer" /><span className="ep">GET /companies/{'{company_id}'}/form-templates</span></div>
            <div className="card">
              {templatesStatus === 'loading' || templatesStatus === 'idle' ? (
                <div className="empty"><b>Loading templates…</b></div>
              ) : templates.length ? (
                <div className="tbl-wrap">
                  <table>
                    <thead><tr><th>Name</th><th>Version</th><th></th></tr></thead>
                    <tbody>
                      {pagedTemplates.map((t) => {
                        const rowBusy = rowBusyId === t.id;
                        return (
                          <tr className="clickable" key={t.id} onClick={() => open(t)}>
                            <td className="strong">{t.name}</td>
                            <td className="mono">v{t.version}{t.is_active && <span className="tag ok" style={{ marginLeft: 6 }}>active</span>}</td>
                            <td>
                              <div className="btn-row">
                                {!t.is_active && (
                                  <button className="btn sm pri" disabled={rowBusy} onClick={(e) => { e.stopPropagation(); onActivateRow(t); }}>Activate</button>
                                )}
                                <button className="btn sm" onClick={(e) => { e.stopPropagation(); open(t); }}>Open</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <div className="empty"><b>No template yet</b>Create the first one on the right.</div>}
              <Pagination page={page} setPage={setPage} pageCount={pageCount} total={total} />
            </div>
          </div>

          <div>
            <div className="sec-h" style={{ marginTop: 0 }}><h3>New template</h3><span className="spacer" /><span className="ep">POST /companies/{'{company_id}'}/form-templates</span></div>
            <div className="card card-b">
              <form onSubmit={submit}>
                <div className="field">
                  <label>Name <span className="req">*</span></label>
                  <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="2026 Enrolment" />
                  <div className="hint">Creates all eleven guaranteed fields automatically (action, email, subsidised product level, effective date, reason, the five profile fields, and NZ citizen/visa eligibility). Becomes active immediately if this company has no active template yet.</div>
                </div>
                <div className="btn-row"><button type="submit" className="btn pri" disabled={busy}>{busy ? 'Creating…' : 'Create template'}</button></div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function TemplateDrawer({ gid, t, onClose, onFollow }) {
  const { templates, templateDetails, loadTemplateDetail, loadTemplates, activateTemplate, updateTemplateName, addField, updateField, removeField } = useIcibAdmin();
  const toast = useToast();
  const [newField, setNewField] = useState(EMPTY_FIELD);
  // ICIB-10g takes a `fields` batch — one call produces one new template
  // version, whether that's one field or several. Build up the batch
  // here (added to, not yet saved) before sending it all in one POST.
  const [pendingFields, setPendingFields] = useState([]);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(t.name);
  const [renaming, setRenaming] = useState(false);
  // ICIB-10h — the one custom field currently open in the inline Edit
  // form: { fieldId, label, optionsText, required }, null otherwise.
  // Label, options and required are saved together in one PATCH, so one
  // edit produces one new template version (not one per click).
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  // Id of the new version a successful field edit created; once it shows
  // up in the refreshed template list, the drawer reopens on it.
  const [followId, setFollowId] = useState(null);

  // loadTemplateDetail isn't stable across renders (IcibAdminContext doesn't
  // memoize it), so key this off t.id alone — including the function itself
  // in the deps would refetch on every render once its own setState fires.
  // ICIB-10c — set when the detail fetch fails (e.g. 404 because this
  // version was deleted after the list loaded). Shown instead of an endless
  // "Loading…"; on 404 the version list is reloaded so the stale row goes.
  const [loadError, setLoadError] = useState(null);
  useEffect(() => {
    let live = true;
    setLoadError(null);
    loadTemplateDetail(t.id).then((res) => {
      if (!live || !res?.error) return;
      setLoadError(res);
      if (res.error === 'not_found') loadTemplates(gid);
    });
    return () => { live = false; };
  }, [t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const detail = templateDetails[t.id];
  const fields = detail?.fields || [];

  useEffect(() => {
    if (!followId || followId === t.id) return;
    const next = templates.find((x) => x.id === followId);
    if (next && onFollow) onFollow(next);
  }, [followId, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every structural edit creates a new version and makes it the
  // company's active template (README §2.7). Follow it: by the returned
  // template_id, or — for an older backend without it — whichever
  // template is now active for this company.
  function followNewVersion(res) {
    const nextId = res?.template_id || templates.find((x) => x.company_id === gid && x.is_active)?.id;
    if (nextId && nextId !== t.id) setFollowId(nextId);
  }

  async function onActivate() {
    const res = await activateTemplate(gid, t.id);
    if (res?.error) toast(res.message || 'Could not activate this template.', 'bad', res.error);
    else toast('Now the active template for this company.', 'ok');
  }

  async function onRename(e) {
    e.preventDefault();
    setRenaming(true);
    const res = await updateTemplateName(t.id, name);
    setRenaming(false);
    if (res?.error) toast(res.message || 'Could not rename this template.', 'bad', res.error);
    else toast('Renamed.', 'ok');
  }

  // Stages one field into the batch — doesn't call the server. A
  // field_key colliding with another already queued (or with an
  // existing field on the template) is left for the server to reject
  // when the batch is actually saved, same as any other validation here.
  // The one check made up front is a dropdown with no options, since the
  // form itself is what decides whether `options` gets sent at all.
  function onQueueField(e) {
    e.preventDefault();
    if (!newField.field_key.trim() || !newField.label.trim()) return;
    const { optionsText, ...field } = newField;
    if (field.field_type === 'dropdown') {
      const options = parseOptions(optionsText);
      if (!options.length) { toast('A dropdown field needs at least one option.', 'bad'); return; }
      field.options = options;
    }
    setPendingFields((q) => [...q, field]);
    setNewField(EMPTY_FIELD);
  }

  function onUnqueueField(i) {
    setPendingFields((q) => q.filter((_, idx) => idx !== i));
  }

  async function onSaveFields() {
    if (!pendingFields.length) return;
    setBusy(true);
    const res = await addField(t.id, pendingFields);
    setBusy(false);
    if (res?.error) { toast(res.message || 'Could not add these fields.', 'bad', res.error); return; }
    toast(`${pendingFields.length} field${pendingFields.length === 1 ? '' : 's'} added — saved as v${res.template_version_after}.`, 'ok');
    setPendingFields([]);
    followNewVersion(res);
  }

  async function onRemoveField(f) {
    if (!window.confirm(`Remove '${f.field_key}'?`)) return;
    const res = await removeField(t.id, f.id);
    if (res?.error) { toast(res.message || 'Could not remove this field.', 'bad', res.error); return; }
    toast(`Field removed — saved as v${res.template_version_after}.`, 'ok');
    followNewVersion(res);
  }

  function onStartEdit(f) {
    setEditing({ fieldId: f.id, label: f.label, optionsText: (f.options || []).join('\n'), required: !!f.required });
  }

  // ICIB-10h — one PATCH with only what changed. `options` is a full
  // replacement list, not a merge. field_key is never sent: it's the key
  // form_data answers are stored under, so it can't change (the backend
  // rejects a different key with field_key_immutable).
  async function onSaveEdit(e, f) {
    e.preventDefault();
    const patch = {};
    const label = editing.label.trim();
    if (!label) { toast('A field needs a label.', 'bad'); return; }
    if (label !== f.label) patch.label = label;
    if (f.field_type === 'dropdown') {
      const options = parseOptions(editing.optionsText);
      if (!options.length) { toast('A dropdown field needs at least one option.', 'bad'); return; }
      if (JSON.stringify(options) !== JSON.stringify(f.options || [])) patch.options = options;
    }
    if (editing.required !== !!f.required) patch.required = editing.required;
    if (!Object.keys(patch).length) { toast('No changes to save.'); return; }
    setSavingEdit(true);
    const res = await updateField(t.id, f.id, patch);
    setSavingEdit(false);
    if (res?.error) { toast(res.message || 'Could not update this field.', 'bad', res.error); return; }
    toast(`'${f.field_key}' updated — saved as v${res.template_version_after}.`, 'ok');
    setEditing(null);
    followNewVersion(res);
  }

  if (loadError) {
    return <div className="note bad"><b>{loadError.error === 'not_found' ? 'This template version no longer exists' : "Couldn't load this template"}</b>{loadError.message || 'Please reload the list.'}</div>;
  }

  return (
    <>
      <div className="sec-h" style={{ marginTop: 0 }}><h3>Name</h3><span className="spacer" /><span className="ep">PATCH /form-templates/{'{id}'}</span></div>
      <form onSubmit={onRename} className="btn-row" style={{ marginBottom: 16 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} style={{ flex: 1 }} />
        <button type="submit" className="btn sm" disabled={renaming || name === t.name}>{renaming ? 'Saving…' : 'Rename'}</button>
      </form>

      <div className="btn-row" style={{ marginBottom: 16 }}>
        {!t.is_active && <button className="btn pri sm" onClick={onActivate}>Make this the active template</button>}
      </div>
      {t.is_active ? (
        <div className="hint" style={{ marginBottom: 16 }}>This is the company's active template. Activating a different version will automatically make this one inactive.</div>
      ) : (
        <div className="hint" style={{ marginBottom: 16 }}>Activating this version will automatically deactivate the company's current active template.</div>
      )}

      <div className="sec-h"><h3>Fields</h3></div>
      {!detail ? (
        <div className="empty"><b>Loading…</b></div>
      ) : (
        <div className="tbl-wrap">
          <table>
            <thead><tr><th>Key</th><th>Label</th><th>Type</th><th>Options</th><th>Required</th><th></th></tr></thead>
            <tbody>
              {fields.map((f) => (
                editing?.fieldId === f.id ? (
                  <tr key={f.id}>
                    <td colSpan={6}>
                      <form onSubmit={(e) => onSaveEdit(e, f)} aria-label={`Edit ${f.field_key}`}>
                        <div className="grid g2">
                          <div className="field">
                            <label>Field key</label>
                            <input value={f.field_key} readOnly disabled />
                            <div className="hint">Can't be changed — it's the key answers are stored under. Change the label instead.</div>
                          </div>
                          <div className="field">
                            <label>Label <span className="req">*</span></label>
                            <input required value={editing.label} onChange={(e) => setEditing((x) => ({ ...x, label: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>Type</label>
                            <input value={f.field_type} readOnly disabled />
                          </div>
                          <div className="field">
                            <label>Required</label>
                            <select value={editing.required ? 'yes' : 'no'} onChange={(e) => setEditing((x) => ({ ...x, required: e.target.value === 'yes' }))}>
                              <option value="no">No</option>
                              <option value="yes">Yes</option>
                            </select>
                          </div>
                        </div>
                        {f.field_type === 'dropdown' && (
                          <div className="field">
                            <label>Options <span className="req">*</span></label>
                            <textarea rows={4} value={editing.optionsText} onChange={(e) => setEditing((x) => ({ ...x, optionsText: e.target.value }))} />
                            <div className="hint">One option per line — replaces the current list.</div>
                          </div>
                        )}
                        <div className="hint" style={{ marginBottom: 8 }}>Saving creates a new version of this template and makes it the active one; the panel switches to it.</div>
                        <div className="btn-row">
                          <button type="submit" className="btn sm pri" disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save changes'}</button>
                          <button type="button" className="btn sm" onClick={() => setEditing(null)}>Cancel</button>
                        </div>
                      </form>
                    </td>
                  </tr>
                ) : (
                  <tr key={f.id}>
                    <td className="mono" style={{ wordBreak: 'break-word' }}>{f.field_key}{GUARANTEED(f) && <span className="tag navy" style={{ marginLeft: 6 }}>guaranteed</span>}</td>
                    <td style={{ wordBreak: 'break-word' }}>{f.label}</td>
                    <td>{f.field_type}</td>
                    <td>
                      {f.field_type !== 'dropdown' ? '—'
                        : f.is_product_field ? <span className="hint">Synced from this company's products</span>
                        : (f.options || []).join(', ')}
                    </td>
                    <td>{f.required ? 'yes' : 'no'}</td>
                    <td>
                      {!GUARANTEED(f) && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                          <button className="btn sm" onClick={() => onStartEdit(f)} disabled={!!editing}>Edit</button>
                          <button className="btn sm danger" onClick={() => onRemoveField(f)} disabled={!!editing}>Remove</button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="sec-h"><h3>Add custom fields</h3><span className="spacer" /><span className="ep">POST /form-templates/{'{id}'}/fields</span></div>
      <div className="hint" style={{ marginBottom: 10 }}>Queue as many as you like, then save them together — one call, one new template version, whether that's one field or several.</div>

      {pendingFields.length > 0 && (
        <div className="tbl-wrap" style={{ marginBottom: 10 }}>
          <table>
            <thead><tr><th>Key</th><th>Label</th><th>Type</th><th>Options</th><th>Required</th><th></th></tr></thead>
            <tbody>
              {pendingFields.map((f, i) => (
                <tr key={i}>
                  <td className="mono">{f.field_key}</td>
                  <td>{f.label}</td>
                  <td>{f.field_type}</td>
                  <td>{f.options ? f.options.join(', ') : '—'}</td>
                  <td>{f.required ? 'yes' : 'no'}</td>
                  <td><button type="button" className="btn sm danger" onClick={() => onUnqueueField(i)}>Remove</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={onQueueField}>
        <div className="grid g2">
          <div className="field">
            <label>Field key <span className="req">*</span></label>
            <input required value={newField.field_key} onChange={(e) => setNewField((f) => ({ ...f, field_key: e.target.value }))} placeholder="cost_centre" />
            <div className="hint">Permanent once saved — answers are stored under this key. The label can be changed later.</div>
          </div>
          <div className="field">
            <label>Label <span className="req">*</span></label>
            <input required value={newField.label} onChange={(e) => setNewField((f) => ({ ...f, label: e.target.value }))} placeholder="Cost Centre" />
          </div>
          <div className="field">
            <label>Type</label>
            <select value={newField.field_type} onChange={(e) => setNewField((f) => ({ ...f, field_type: e.target.value }))}>
              {FIELD_TYPES.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Required</label>
            <select value={newField.required ? 'yes' : 'no'} onChange={(e) => setNewField((f) => ({ ...f, required: e.target.value === 'yes' }))}>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </div>
        </div>
        {newField.field_type === 'dropdown' && (
          <div className="field">
            <label>Options <span className="req">*</span></label>
            <textarea required rows={4} value={newField.optionsText} onChange={(e) => setNewField((f) => ({ ...f, optionsText: e.target.value }))} placeholder={'Full-time\nPart-time\nCasual'} />
            <div className="hint">One option per line. These are the exact values an answer is checked against at intake.</div>
          </div>
        )}
        <div className="btn-row">
          <button type="submit" className="btn sm">+ Add to batch</button>
          <button type="button" className="btn pri" disabled={busy || !pendingFields.length} onClick={onSaveFields}>
            {busy ? 'Saving…' : `Save ${pendingFields.length || ''} field${pendingFields.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </form>
    </>
  );
}

export default FormsView;
